import React, { useMemo, useState } from 'react';
import mixpanel from 'mixpanel-browser';

import { useInventory } from '@services/stock';
import { useDeliveryChannels, useEnabledChannels } from '@services/channels';
import { useShop } from '@services/ShopProvider';

import { useIsChain } from '@utils/useIsChain';

import { EmptyResultPlaceHolder, TablePlaceholder } from '@components/Table/TablePlaceholder';
import ProductUploadModal from '../../components/ProductUploadModal';
import { BarcodeVisibilityModal } from '../../components/BarcodeVisibilityModal';
import { HIDE_OPERATION, UNHIDE_OPERATION } from '../../components/BarcodeVisibilityModal/operations';
import { QuantityBufferModal } from '../../components/QuantityBufferModal';
import ActionBar from './ActionBar';
import DesktopTable from './DesktopTable';
import MobileTable from './MobileTable';
import TablePagination from './TablePagination';

import styles from './InventoryViewer.module.scss';
import StockLevelsDrawer from '../../InventoryItem/StockLevels/StockLevelsDrawer';

const InventoryViewer = () => {
	const isChain = useIsChain();
	const { shop, organisation } = useShop();
	const {
		page: currentPage,
		totalPages,
		changePage,
		isLoading: isInventoryLoading,
		isEmpty,
		setSearchInput,
		isValidating,
		mutate
	} = useInventory(organisation.id, shop.id);

	const [bulkHideModal, setBulkHideModal] = useState(false);
	const [bulkUnhideModal, setBulkUnhideModal] = useState(false);
	const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);
	const [quantityBufferModal, setQuantityBufferModal] = useState(false);
	const [disableActions, setDisableActions] = useState(false);
	const [stockLevelsBarcode, setStockLevelsBarcode] = useState(null);

	const channels = useEnabledChannels();
	const lastMileChannels = useDeliveryChannels();
	const hasLastMileChannels = !!lastMileChannels?.length;
	const visibilityChannels = useMemo(
		() => channels?.filter((ch) => ch.productSpecificVisibility) ?? [],
		[channels]
	);

	const handleSearchChange = (e) => {
		const input = e.target.value;
		setSearchInput(input);
		const tracking = {
			input,
			distinct_id: shop.id,
			'Shop ID': shop.id,
			'Shop LegacyId': shop.legacyId
		};
		if (!isChain) {
			tracking['Shop ID'] = shop.id;
			tracking['Shop LegacyId'] = shop.legacyId;
			tracking['distinct_id'] = shop.id;
		} else {
			tracking['Organisation ID'] = organisation.id;
			tracking['distinct_id'] = organisation.id;
		}
		mixpanel.track('Inventory search', tracking);
	};

	return (
		<>
			<BarcodeVisibilityModal
				open={bulkHideModal}
				close={() => setBulkHideModal(false)}
				shop={shop}
				mutate={mutate}
				operation={HIDE_OPERATION}
				availableChannels={visibilityChannels}
			/>
			<BarcodeVisibilityModal
				open={bulkUnhideModal}
				close={() => setBulkUnhideModal(false)}
				shop={shop}
				mutate={mutate}
				operation={UNHIDE_OPERATION}
				availableChannels={visibilityChannels}
			/>
			<ProductUploadModal open={bulkUploadModalOpen} close={() => setBulkUploadModalOpen(false)} />
			<QuantityBufferModal open={quantityBufferModal} close={() => setQuantityBufferModal(false)} shop={shop} />
			<StockLevelsDrawer barcode={stockLevelsBarcode} setBarcode={setStockLevelsBarcode} />

			<ActionBar
				shop={shop}
				setBulkHideModal={setBulkHideModal}
				setBulkUnhideModal={setBulkUnhideModal}
				setBulkUploadModalOpen={setBulkUploadModalOpen}
				setQuantityBufferModal={setQuantityBufferModal}
				handleSearchChange={handleSearchChange}
				disabled={disableActions}
			/>

			<div className={styles.inventoryContainer}>
				{isEmpty ? (
					<EmptyResultPlaceHolder />
				) : isInventoryLoading || isValidating ? (
					<TablePlaceholder />
				) : (
					<>
						<DesktopTable
							setDisableActions={setDisableActions}
							setStockLevelsBarcode={setStockLevelsBarcode}
							channels={channels}
							hasLastMileChannels={hasLastMileChannels}
						/>
						<MobileTable hasLastMileChannels={hasLastMileChannels} setStockLevelsBarcode={setStockLevelsBarcode} />
						<TablePagination pages={totalPages} currentPage={currentPage} onPageChange={changePage} />
					</>
				)}
			</div>
		</>
	);
};

export default InventoryViewer;
